import {gsap} from "gsap";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {SoundManager} from "../../../sound/SoundManager";
import {SoundConfig} from "../../../sound/SoundConfig";
import {PunkRocker} from "../../../PunkRocker";
import {GameData} from "../../../server/data/GameData";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {BitmapCountUp} from "@nolimitcity/slot-game/bin/component/BitmapCountUp";
import {GameAssets} from "../../../config/texture/GameAssets";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {TextStyleOptions} from "../../../../core/setting/text/IText";
import {FontStyle, FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {GameConfig} from "../../../config/base/GameConfig";

export class CapWinView extends PIXI.Container {
    private _spine: PIXI.spine.Spine | null;
    private _spineContainer: PIXI.Sprite;
    private _button: ContinueButton;
    private _clickCallback: () => void;
    private _countUp: BitmapCountUp;
    private _gameData: GameData;
    private _isTotalWin: boolean = false;
    private _textStyle: TextStyleOptions = {
        dropShadow: true,
        dropShadowAngle: 8,
        dropShadowBlur: 10,
        dropShadowDistance: 2,
        fontFamily: "Open Sans",
        fontStyle: FontStyle.ITALIC,
        fontWeight: FontWeight.EXTRA_BOLD,
        fontSize: 68,
        fill: [
            "white",
            // "#fffb00",
            // "#ff9300"
        ]
    };

    constructor() {
        super();
        this.name = "WinCapView";

        this.init();
    }

    setGamedData(data: GameData): void {
        this._gameData = data;
        this._isTotalWin = data.mode !== GameMode.NORMAL || data.nextMode !== GameMode.NORMAL;
    }

    public onResize(): void {
        if (!this.visible) {
            return;
        }

        if (NolimitApplication.isLandscape) {
            this._button.position.set(360, 615);
        } else {
            const centerBottom: number = (NolimitApplication.screenBounds.bottom - 610) * 0.5 + 610;
            this._button.position.set(360, centerBottom - 80);
        }
    }

    public show(clickCallback: () => void): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        if (this._spine) {
            this._spine.destroy();
            this._spine = null;
        }

        this._spine = new PIXI.spine.Spine(PunkRocker.assetLoader.getSpineAsset("winCap"));
        this.onResize();
        PunkRocker.screenDimmer.show();
        PunkRocker.jumpWilds.makeAllDead();
        GameConfig.FORCE_BRING_BOTTOM_TRAIN = false;
        this._button.enable(false);
        this._countUp.alpha = 0;
        this._clickCallback = clickCallback;
        this.alpha = 0;

        PunkRocker.keypadPlugin.setZeroBetSpinCounter(-1);

        const countUpDuration: number = 6.742,
            countUpAnimationTabDuration: number = (this._spine!.skeleton.data.findAnimation("Wincap_Countup").duration);

        tl.add(() => {
            this._countUp.text = "";
            this._countUp.position.set(360, 360);
            this.visible = true;
            this.onResize();
            SoundManager.stopAmbientLoop();

            SoundManager.playSoundEffect(SoundConfig.WIN_CAP_START);
            this._spineContainer.addChild(this._spine!);
            this._spine!.state.setAnimation(0, "Wincap_Countup", false);
        }, 0);
        tl.add([
            PunkRocker.screenDimmer.fadeIn(),
            gsap.to([this, this._countUp], {alpha: 1, duration: 0.5}),
            <gsap.core.Timeline><unknown>this._countUp.getCountUpAnimation(0, this._gameData?.raw.accumulatedRoundWin!, countUpDuration)
        ], 0);

        tl.add(() => {
            this._spine!.state.setAnimation(0, "Wincap_Intro", false);
        }, (countUpAnimationTabDuration));

        tl.add(() => {
            SoundManager.playSoundEffect(SoundConfig.WIN_CAP_MUSIC, Number.MAX_VALUE);
        }, (countUpDuration));

        tl.add(() => {
            this._spine!.state.setAnimation(0, "Wincap_Loop", true);
            this.addChild(this._button);
        }, (countUpDuration + 0.5));
        tl.to(this._button, {alpha: 1, duration: 0.5}, "<");

        tl.add(() => {
            this._button.enable(true);
        });

        return tl;
    }

    private init(): void {
        this._spineContainer = new PIXI.Sprite();
        const capWinText: PIXI.Text = new PIXI.Text("We Want You", this._textStyle);
        capWinText.position.x = 0;
        capWinText.position.y = -160;
        capWinText.anchor.set(0.5);
        this.addChild(this._spineContainer);
        this._spineContainer.addChild(capWinText);
        this._spineContainer.position.set(360, 360);
        this._button = new ContinueButton();
        this._button.addClickCallback(() => this.onButtonClick());
        this._button.pivot.set(this._button.width * 0.5, this._button.height * 0.5);
        this._button.enable(false);
        this._button.alpha = 0;
        this.onResize();

        this._countUp = new BitmapCountUp({
            fontName: GameAssets.BITMAP_FONT_WIN_COUNT_UP_LOW,
            fontSize: 68,
            align: "center",
            maxWidth: 580,
            letterSpacing: 5
        });
        this._countUp.shrinkSize = true;
        this._countUp.position.set(360, 578);
        this._countUp.text = "";
        this.addChild(this._countUp);
        this.visible = false;
    }

    private onButtonClick(): void {
        const hideWinCapTL = gsap.timeline();
        hideWinCapTL.add(() => {
            this._button.enable(false);
            PunkRocker.keypadPlugin.setWin(this._gameData.raw.accumulatedRoundWin, this._isTotalWin, this._gameData.Win.isWinBelowStake());
            PunkRocker.keypadPlugin.show(0.2);
            SoundManager.playSoundEffect(SoundConfig.WIN_CAP_ENDS);
            SoundManager.stopSoundEffect(SoundConfig.WIN_CAP_MUSIC);
            SoundManager.playAmbientLoop("NORMAL");
            SoundManager.fadeAmbienceLoop(1, 1000);
        });

        hideWinCapTL.add([
            PunkRocker.screenDimmer.fadeOut(),
            gsap.to(this, {alpha: 0, duration: 0.2}),
            gsap.to([this._button, this._countUp], {alpha: 0, duration: 0.2}),
            gsap.to(this._spine, {alpha: 1, duration: 0.2}),
            () => {
                PunkRocker.noService.toggleServiceBoard(false);
                PunkRocker.waysCounter.hardSetValue(Math.pow(3, 6));
                PunkRocker.waysCounter.totalWinMorse(0.5, true);
                PunkRocker.trainsManager.topTrain.setTrainAlpha(false, 0.5);
                PunkRocker.trainsManager.bottomTrain.setTrainAlpha(false, 0.5);

                // unnecessary transition if cap win is outside bonus game, so avoiding it.
                if (this._gameData.mode.indexOf("FREESPIN") > -1) {
                    PunkRocker.background.fromBonusToMain();
                }
            }
        ]);

        hideWinCapTL.add(() => {
            this.visible = false;
            this._spineContainer.removeChildren();
            PunkRocker.screenDimmer.hide();
            this._spine!.destroy();
            this._spine = null;
        });

        // #331
        hideWinCapTL.add(() => {
            PunkRocker.waysCounter.counterActive(true);

            this._clickCallback();
            this._clickCallback = () => {
            };
        }, "+=0.3");
    }
}
